/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useParams } from 'react-router';
import ReactDOM from 'react-dom';
import { useDrop } from 'react-dnd';
import { useSnackbar } from 'notistack';
import L, {
  map,
  CRS,
  imageOverlay,
  point,
  marker,
  icon,
  Marker,
  LatLngBounds,
} from 'leaflet';
import 'leaflet-responsive-popup';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import update from 'immutability-helper';
import { get, head, uniqBy } from 'lodash';
import axios from 'axios';
import SVG from 'react-inlinesvg';
import { Divider } from '@material-ui/core';
import { getUrl } from '../../utils/S3Utils';
import { CardHeader } from '../../components/CardHeader';
import {
  GeoAnchorCard,
  ThingCard,
  InstalledThingCard,
} from '../../components/ThingCard';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
import { Anchor, Scale, GeoAnchor, Bridge } from './markerTemplate';
import { Button } from '../../components/Button';
import { Loading } from '../../components/Loading';
import EditFloorPlanSideMenu from './components/EditFloorPlanSideMenu';
import EditZoneSideMenu from './components/EditZoneSideMenu';
import AlertViewSideMenu from './components/AlertViewSideMenu';
import { MotionContainer } from '../../components/MotionContainer';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useWebsocket } from '../../contexts/WebsocketContext';
import { shallowEqual, useSelector } from 'react-redux';
import { SearchInput } from '../../components/SearchInput';
import ErrorDialog from '../../components/ErrorDialog';

function getSchema(intl) {
  return Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[!-ÿ\s]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.NAME.REGEX' })
      ),
    description: Yup.string(),
    externalId: Yup.string()
      .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
      .matches(
        /^[a-z0-9-.]*$/,
        intl.formatMessage({ id: 'ADMINISTRATION.CODE.REGEX' })
      ),
    floorPlanURI: Yup.string().required(
      intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })
    ),
  });
}

function overrideVortex(latlng) {
  var markersLength = this._markers.length;
  // markersLength must be greater than or equal to 2 before intersections can occur

  if (
    markersLength >= 2 &&
    !this.options.allowIntersection &&
    this._poly.newLatLngIntersects(latlng)
  ) {
    this._showErrorTooltip();
    return;
  } else if (this._errorShown) {
    this._hideErrorTooltip();
  }

  this._markers.push(this._createMarker(latlng));

  this._poly.addLatLng(latlng);
  if (this._poly.getLatLngs().length === 2) {
    this._map.addLayer(this._poly);
  }

  this._vertexChanged(latlng, true);
  markersLength = this._markers.length;
  if (markersLength === 2) {
    this._fireCreatedEvent();
    this.disable();
  }
}

function FloorPlansEdit({ intl, history }) {
  const { areaId, facilityId, floorPlanId } = useParams();
  const loggedUser = useSelector(({ auth }) => auth.user, shallowEqual);
  const [floorPlan, setFloorPlan] = useState({});
  const [geoAnchors, setGeoAnchors] = useState([]);
  const [availableThings, setAvailableThings] = useState([]);
  const [floorPlanThings, setFloorPlanThings] = useState([]);
  const [filteredThings, setFilteredThings] = useState([]);
  const inputController = useRef('');
  const { websocket, message, isReady } = useWebsocket();
  const zonesRef = useRef([]);
  const tagsRef = useRef([]);
  const scaleRef = useRef(1);
  const didLoadFloorPlanThings = useRef(false);
  const [networks, setNetworks] = useState([]);
  const selectedThing = useRef(null);
  const [url, setUrl] = useState('');
  const [image, setImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [leaflet, setLeaflet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [originMarker, setOriginMarker] = useState(null);
  const [open, setOpen] = useState({
    edit: false,
    zones: false,
    anchors: false,
    alerts: false,
  });
  const [pendencies, setPendencies] = useState({
    geoAnchors: false,
    initiator: false,
    bridge: false,
    processingGroup: false,
  });
  const mapRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const transformation = L.transformation(1, 0, 1, 0);
  const { setPaths } = useBreadcrumb();

  const [boxes, setBoxes] = useState({
    1: { top: 0, left: 0, id: -1 },
    2: { top: 45, left: 0, id: -2 },
    3: { top: 90, left: 0, id: -3 },
  });

  function setSelectedThing(val) {
    selectedThing.current = val;
  }

  const moveBox = useCallback(
    (id, left, top) => {
      setBoxes(
        update(boxes, {
          [id]: {
            $merge: { left, top },
          },
        })
      );
    },
    [boxes, setBoxes]
  );

  const [, drop] = useDrop(
    {
      accept: 'THING',
    },
    [moveBox]
  );

  useEffect(() => {
    async function getFloorPlan() {
      setIsLoading(true);
      try {
        const errors = {};
        const [
          floorPromise,
          thingPromise,
          availableThingPromise,
          floorPlanThingsPromise,
          networksPromise,
        ] = await Promise.all([
          axios.get(
            `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}`
          ),
          axios.get(
            `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors`
          ),
          axios.get(`/things?isAvailable=true`),
          axios.get(`/things?floorPlanId=${floorPlanId}`),
          axios.get(`/networks`),
        ]);
        if (networksPromise.data) setNetworks(networksPromise.data);
        if (floorPromise.data) {
          setFloorPlan({
            ...floorPromise.data,
            processingGroupId: floorPromise.data.processingGroupId || '',
          });
          scaleRef.current = floorPromise.data.scale;
          const origin = floorPlanThingsPromise.data.find(
            x =>
              x.leafletCoordinatesInMeters?.x === 0 &&
              x.leafletCoordinatesInMeters?.y === 0
          );
          if (origin) setOriginMarker(origin);
          const signedUrl = await getUrl(
            floorPromise.data.croppedFloorPlanURI ||
              floorPromise.data.floorPlanURI
          );
          setUrl(signedUrl);
          if (!floorPromise.data.processingGroupId)
            errors.processingGroup = true;
          if (thingPromise.data.length < 3) errors.geoAnchors = true;
          setGeoAnchors(thingPromise.data);
          setPaths([
            { title: intl.formatMessage({ id: 'FLOORPLANS' }) },
            lastPage(),
            {
              title: floorPromise.data.areaName,
              pathname: `/geo-planner/areas/${areaId}`,
            },
            {
              title: floorPromise.data.facilityName,
              pathname: `/geo-planner/areas/${areaId}/facilities/${facilityId}`,
            },
            {
              title: floorPromise.data.name,
              pathName: `/geo-planner/areas/${areaId}/facilities/${facilityId}/floorPlans/${floorPlanId}`,
            },
          ]);
        }
        if (availableThingPromise.data)
          setAvailableThings(availableThingPromise.data);
        if (floorPlanThingsPromise.data) {
          const hasAnchor = floorPlanThingsPromise.data.some(x =>
            x.model.startsWith('swxa')
          );
          errors.initiator = floorPlanThingsPromise.data.find(
            x => x.role === 'initiator'
          )
            ? false
            : hasAnchor;
          errors.bridge = floorPlanThingsPromise.data.find(
            x => x.role === 'bridge'
          )
            ? false
            : hasAnchor;
          setFloorPlanThings(floorPlanThingsPromise.data);
          if (!floorPlanThingsPromise.data.length)
            didLoadFloorPlanThings.current = true;
        }
        if (
          errors.initiator ||
          errors.bridge ||
          errors.geoAnchors ||
          errors.processingGroup
        ) {
          setPendencies(state => ({
            ...state,
            ...errors,
          }));
          setOpen(state => ({ ...state, alerts: true }));
        }
      } finally {
        setIsLoading(false);
      }
    }

    getFloorPlan();
  }, []);

  useEffect(() => {
    if (isReady) {
      websocket.send(
        JSON.stringify({
          event: 'rtls-live-indoor-position',
          type: 'subscribe',
          data: {
            floorPlanId,
            businessUserId: loggedUser.userId,
            businessEntityId: loggedUser.entityId,
          },
        })
      );
    }

    return () => {
      if (isReady) {
        websocket.send(
          JSON.stringify({
            event: 'rtls-live-indoor-position',
            type: 'unsubscribe',
            data: {
              floorPlanId,
              businessUserId: loggedUser.userId,
              businessEntityId: loggedUser.entityId,
            },
          })
        );
      }
    };
  }, [isReady]);

  useEffect(() => {
    if (
      !pendencies.bridge &&
      !pendencies.geoAnchors &&
      !pendencies.initiator &&
      !pendencies.processingGroup
    ) {
      setOpen(state => ({ ...state, alerts: false }));
    }
  }, [pendencies]);

  useEffect(() => {
    if (url) {
      validateImageData();
      const leafMap = map('map', {
        crs: CRS.Simple,
        bounceAtZoomLimits: true,
        center: [0, 0],
        // zoom: 2,
        minZoom: 1,
        maxZoom: 5,
        zoomSnap: 1,
        editable: true,
      });
      mapRef.current.ondragover = e => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      };
      setLeaflet(leafMap);
    }
  }, [url]);

  useEffect(() => {
    if (message?.event === 'rtls-live-indoor-position' && leaflet) {
      const { data } = message;
      const tagPoint = L.point(data.position.x, data.position.y);
      if (originMarker) {
        const markToUpdate = tagsRef.current.find(
          x => x.tagName === data.tagName
        );
        const latLng = originMarker.leafletCoordinates;
        const originPoint = L.point(latLng.lat, latLng.lng);
        const unstransformed = transformation.untransform(
          tagPoint,
          scaleRef.current
        );
        const coordinatesInMap = unstransformed.add(originPoint);
        if (markToUpdate) {
          markToUpdate.setLatLng(
            L.latLng(coordinatesInMap.x, coordinatesInMap.y)
          );
          clearTimeout(markToUpdate.timer);
          markToUpdate.timer = setTimeout(() => {
            tagsRef.current = tagsRef.current.filter(
              x => x.tagName !== data.tagName
            );
            markToUpdate.remove();
          }, 10000);
        } else {
          const mark = marker(
            L.latLng(coordinatesInMap.x, coordinatesInMap.y),
            {
              icon: L.divIcon({
                className: 'pulse-anchor',
                iconSize: [25, 25],
              }),
              shadowPane: true,
              interactive: false,
              zIndexOffset: 10000,
            }
          );
          tagsRef.current.push(mark);
          mark.tagName = data.tagName;
          mark.bindTooltip(data.tagName, { permanent: true });
          mark.addTo(leaflet);
          mark.timer = setTimeout(() => {
            tagsRef.current = tagsRef.current.filter(
              x => x.tagName !== data.tagName
            );
            mark.remove();
          }, 10000);
        }
      }
    }
  }, [message]);

  useEffect(() => {
    if (leaflet && image) {
      const bounds = new LatLngBounds([
        [0, 0],
        [image.height / 10, image.width / 10],
      ]);

      if (!Object.keys(leaflet._layers).length) {
        imageOverlay(url, bounds).addTo(leaflet);
        leaflet.fitBounds(bounds);
      }

      mapRef.current.ondrop = async e => {
        const val = e.target.getBoundingClientRect();
        const { x, y } = val;
        e.preventDefault();
        const coordinates = leaflet.containerPointToLatLng(
          point([e.clientX - x, e.clientY - y])
        );
        if (bounds.contains(coordinates)) {
          const mark = marker(coordinates, {
            icon: icon({
              iconUrl: toAbsoluteUrl(
                selectedThing.current
                  ? '/media/svg/senseworks/anchor-icon.svg'
                  : '/media/svg/senseworks/geo-anchor-icon.svg'
              ),
              iconSize: [leaflet.getZoom() * 10, leaflet.getZoom() * 10],
            }),
            draggable: true,
            shadowPane: true,
          });
          mark.addTo(leaflet);
          let lastValidPosition;
          mark.on('dragstart', e => {
            lastValidPosition = e.target.getLatLng();
          });
          mark.on(
            'dragend',
            e => {
              const latLng = e.target.getLatLng();
              if (!bounds.contains(latLng)) {
                mark.setLatLng(lastValidPosition);
                return;
              }
              if (!selectedThing.current && mark.databaseId) {
                enqueueSnackbar(intl.formatMessage({ id: 'UPDATING_SHADOW' }), {
                  variant: 'success',
                  autoHideDuration: 2000,
                });
                const geoAnchor = mark.geoAnchor;
                const values = {
                  lat: geoAnchor.lat,
                  long: geoAnchor.long,
                  mapCoordinates: mark._latlng,
                  leafletCoordinatesInMeters: {
                    x: null,
                    y: null,
                  },
                };
                const transform = transformLatLng(true);
                if (transform) {
                  const point = transform(mark);
                  values.leafletCoordinatesInMeters.x = point.x;
                  values.leafletCoordinatesInMeters.y = point.y;
                }
                axios.put(
                  `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors/${mark.databaseId}`,
                  values
                );
              }
            },
            mark
          );
          setIsDragging(false);
          if (selectedThing.current) {
            const { lat, lng } = mark.getLatLng();
            selectedThing.current.leafletCoordinates = {
              lat,
              lng,
            };
            selectedThing.current.floorPlanId = +floorPlanId;
            mark.anchor = selectedThing.current;
            const transform = transformLatLng();
            let point = transform(mark);
            const layers = [];
            leaflet.eachLayer(lay => {
              if (lay.anchor) layers.push(lay);
            });
            if (selectedThing.current) {
              const availableThingsLocal = availableThings.filter(
                x => x.id !== selectedThing.current.id
              );
              setAvailableThings(val =>
                val.filter(x => x.id !== selectedThing.current.id)
              );
              const floorPlanThingsLocal = floorPlanThings.concat(mark.anchor);
              setFloorPlanThings(val => val.concat(mark.anchor));
              if (filteredThings.length)
                setFiltered(
                  availableThingsLocal,
                  floorPlanThingsLocal,
                  inputController.current
                );
            }
            const anchor = mark.anchor;
            if (layers.length > 1) {
              const point = L.point(lat, lng);
              let originAnchor;
              leaflet.eachLayer(lay => {
                if (lay.anchor) {
                  const localAnchor = lay.anchor;
                  if (
                    localAnchor.leafletCoordinatesInMeters?.x === 0 &&
                    localAnchor.leafletCoordinatesInMeters?.y === 0 &&
                    localAnchor.id !== anchor.id
                  ) {
                    originAnchor = localAnchor;
                  }
                }
              });
              const originPoint = L.point(
                originAnchor.leafletCoordinates.lat,
                originAnchor.leafletCoordinates.lng
              );

              const dist = transformation.transform(
                point.subtract(originPoint),
                scaleRef.current
              );
              updateLocation(anchor, mark, dist);
            } else {
              const { data } = await axios.get(
                `/thing-shadows/${anchor.thingName}`
              );
              axios.put(`/things/${anchor.thingName}`, {
                lat: lat,
                lng: lng,
                floorPlanId,
                leafletCoordinatesInMeters: {
                  x: 0,
                  y: 0,
                },
              });
              updateZones(mark);
              updateGeoAnchorsLocation(mark);
              if (data.uwb?.state) {
                const shadow = {
                  ...data.uwb.state.desired,
                  classic: data.classic?.state?.desired,
                  connectivity: data.connectivity?.state?.desired ?? {},
                };
                delete shadow.label;
                axios.put(`/thing-shadows/${anchor.thingName}`, {
                  oldValues: {
                    ...shadow,
                    positioning: {},
                  },
                  newValues: {
                    ...shadow,
                    positioning: {
                      x: 0,
                      y: 0,
                      z: 0,
                    },
                    enabled: true,
                    networkId: networks[0].networkId,
                  },
                });
              }
              mark.anchor.leafletCoordinatesInMeters = {
                x: 0,
                y: 0,
              };
              mark.anchor.leafletCoordinates = {
                lat,
                lng,
              };
            }
            let lastValidPosition;
            mark.on('dragstart', e => {
              lastValidPosition = e.target.getLatLng();
            });
            mark.on(
              'dragend',
              async e => {
                const transform = transformLatLng();
                point = transform(e.target);
                const latLng = e.target.getLatLng();
                if (!bounds.contains(latLng)) mark.setLatLng(lastValidPosition);
                else {
                  const { data } = await axios.get(
                    `/thing-shadows/${anchor.thingName}`
                  );
                  const { lat, lng } = mark.getLatLng();
                  enqueueSnackbar(
                    intl.formatMessage({ id: 'UPDATING_SHADOW' }),
                    {
                      variant: 'success',
                      autoHideDuration: 2000,
                    }
                  );
                  const promises = [
                    axios.put(`/things/${anchor.thingName}`, {
                      lat: lat,
                      lng: lng,
                      floorPlanId,
                      leafletCoordinatesInMeters: {
                        x: point.x,
                        y: point.y,
                      },
                    }),
                  ];
                  const isOrigin =
                    data.uwb?.state?.desired?.positioning?.x === 0 &&
                    data.uwb?.state?.desired?.positioning?.y === 0;
                  if (data.uwb?.state) {
                    const shadow = {
                      ...data.uwb.state.desired,
                      classic: data.classic?.state?.desired,
                      connectivity: data.connectivity?.state?.desired ?? {},
                    };
                    delete shadow.label;
                    promises.push(
                      axios.put(`/thing-shadows/${anchor.thingName}`, {
                        oldValues: {
                          ...shadow,
                          positioning: {},
                        },
                        newValues: {
                          ...shadow,
                          positioning: {
                            x: +point.x.toFixed(3),
                            y: +point.y.toFixed(3),
                            z: shadow.positioning.z,
                          },
                        },
                      })
                    );
                  }
                  await Promise.all(promises);
                  if (isOrigin) {
                    updateLocations(mark);
                    updateZones(mark);
                    updateGeoAnchorsLocation(mark);
                  }
                }
              },
              mark
            );
            createAnchorMarkers(mark, leaflet);
          } else createMarkers(mark, leaflet);
        }
      };
    }
  }, [leaflet, image, transformation]);

  useEffect(() => {
    if (leaflet && image && geoAnchors.length) {
      const bounds = new LatLngBounds([
        [0, 0],
        [image.height / 10, image.width / 10],
      ]);
      geoAnchors.forEach(geo => {
        const mark = marker(geo.mapCoordinates, {
          icon: icon({
            iconUrl: toAbsoluteUrl('/media/svg/senseworks/geo-anchor-icon.svg'),
            iconSize: [leaflet.getZoom() * 10, leaflet.getZoom() * 10],
          }),
          draggable: hasPermission('floor-plans/*/things*', 'U'),
          shadowPane: true,
        });
        mark.databaseId = geo.id;
        mark.geoAnchor = geo;
        mark.addTo(leaflet);
        let lastValidPosition;
        mark.on('dragstart', e => {
          lastValidPosition = e.target.getLatLng();
        });
        mark.on(
          'dragend',
          async e => {
            const latLng = e.target.getLatLng();
            if (!bounds.contains(latLng)) mark.setLatLng(lastValidPosition);
            else if (mark.geoAnchor) {
              const latLng = JSON.parse(mark.geoAnchor.coordinates).coordinates;
              enqueueSnackbar(intl.formatMessage({ id: 'UPDATING_SHADOW' }), {
                variant: 'success',
                autoHideDuration: 2000,
              });
              const values = {
                lat: latLng[0],
                long: latLng[1],
                mapCoordinates: mark._latlng,
                leafletCoordinatesInMeters: null,
              };
              const transform = transformLatLng(true);
              if (transform) {
                const point = transform(mark);
                values.leafletCoordinatesInMeters = {
                  x: point.x,
                  y: point.y,
                };
              }
              await axios.put(
                `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors/${mark.databaseId}`,
                values
              );
            }
          },
          mark
        );
        createMarkers(mark, leaflet, JSON.parse(geo.coordinates));
      });
    }
  }, [leaflet, geoAnchors, image]);

  useEffect(() => {
    if (
      leaflet &&
      image &&
      floorPlanThings.length &&
      !didLoadFloorPlanThings.current
    ) {
      didLoadFloorPlanThings.current = true;
      const bounds = new LatLngBounds([
        [0, 0],
        [image.height / 10, image.width / 10],
      ]);
      floorPlanThings.forEach(anc => {
        const mark = marker(anc.leafletCoordinates, {
          icon: icon({
            iconUrl: toAbsoluteUrl('/media/svg/senseworks/anchor-icon.svg'),
            iconSize: [leaflet.getZoom() * 10, leaflet.getZoom() * 10],
          }),
          draggable: hasPermission('things*', 'U'),
          shadowPane: true,
        });
        mark.anchor = anc;
        mark.addTo(leaflet);
        let lastValidPosition;
        mark.on('dragstart', e => {
          lastValidPosition = e.target.getLatLng();
        });
        mark.on(
          'dragend',
          async e => {
            const latLng = e.target.getLatLng();
            if (!bounds.contains(latLng)) mark.setLatLng(lastValidPosition);
            else {
              const { data } = await axios.get(
                `/thing-shadows/${anc.thingName}`
              );
              const { lat, lng } = mark.getLatLng();
              enqueueSnackbar(intl.formatMessage({ id: 'UPDATING_SHADOW' }), {
                variant: 'success',
                autoHideDuration: 2000,
              });
              const transform = transformLatLng();
              let point = transform(mark);
              const isOrigin =
                data.uwb?.state?.desired?.positioning?.x === 0 &&
                data.uwb?.state?.desired?.positioning?.y === 0;
              const promises = [
                axios.put(`/things/${anc.thingName}`, {
                  lat: lat,
                  lng: lng,
                  floorPlanId,
                  leafletCoordinatesInMeters: {
                    x: isOrigin ? 0 : point.x,
                    y: isOrigin ? 0 : point.y,
                  },
                }),
              ];
              if (data.uwb?.state) {
                const shadow = {
                  ...data.uwb.state.desired,
                  classic: data.classic?.state?.desired,
                  connectivity: data.connectivity?.state?.desired ?? {},
                };
                delete shadow.label;
                promises.push(
                  axios.put(`/thing-shadows/${anc.thingName}`, {
                    oldValues: {
                      ...shadow,
                      positioning: {},
                    },
                    newValues: {
                      ...shadow,
                      positioning: {
                        x: isOrigin ? 0 : +point.x.toFixed(3),
                        y: isOrigin ? 0 : +point.y.toFixed(3),
                        z: shadow.positioning.z,
                      },
                    },
                  })
                );
              }
              if (isOrigin) {
                updateLocations(mark);
                updateZones(mark);
                updateGeoAnchorsLocation(mark);
              }
              await Promise.all(promises);
            }
          },
          mark
        );
        createAnchorMarkers(mark, leaflet);
      });
    }
  }, [leaflet, floorPlanThings, image]);

  function afterUpdateScale(scale) {
    scaleRef.current = scale;
    leaflet.eachLayer(lay => {
      if (lay instanceof Marker && lay.anchor) {
        const anchor = lay.anchor;
        const latLng = lay.getLatLng();
        if (
          anchor.leafletCoordinatesInMeters?.x !== 0 &&
          anchor.leafletCoordinatesInMeters?.y !== 0
        ) {
          let dist;
          const originAnchor = floorPlanThings.find(
            x =>
              x.leafletCoordinatesInMeters?.x === 0 &&
              x.leafletCoordinatesInMeters?.y === 0
          );
          if (originAnchor) {
            const originPoint = L.point(
              originAnchor.leafletCoordinates.lat,
              originAnchor.leafletCoordinates.lng
            );
            const layerPoint = L.point(latLng.lat, latLng.lng);
            const afterSubtract = layerPoint.subtract(originPoint);
            dist = transformation.transform(afterSubtract, scale);
          } else {
            dist = L.point(latLng.lat, latLng.lng);
          }
          updateLocation(anchor, lay, dist);
        }
      }
    });
    zonesRef.current.map(z => {
      z.geoJsonFromOrigin = getDistFromOrigin(z);
      return axios.put(
        `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/zones/${z.id}`,
        z
      );
    });
  }

  function lastPage() {
    const from = history.location?.state?.from;
    if (from === 'facilities-list')
      return {
        title: intl.formatMessage({ id: 'FACILITIES' }),
        pathname: '/geo-planner/facilities',
      };
    else if (from === 'zones')
      return {
        title: intl.formatMessage({ id: 'ZONES' }),
        pathname: '/geo-planner/zones',
      };
    else
      return {
        title: intl.formatMessage({ id: 'AREAS.TITLE' }),
        pathname: '/geo-planner/areas',
      };
  }

  function transformLatLng(isGeo) {
    let originAnchor;
    leaflet.eachLayer(lay => {
      if (lay.anchor) {
        const anchor = lay.anchor;
        if (
          anchor.leafletCoordinatesInMeters?.x === 0 &&
          anchor.leafletCoordinatesInMeters?.y === 0
        ) {
          originAnchor = lay;
        }
      }
    });
    if (originAnchor) {
      return layer => {
        const { lat, lng } = originAnchor.getLatLng();
        const originPoint = L.point(lat, lng);
        const layerPoint = L.point(layer._latlng.lat, layer._latlng.lng);
        const afterSubtract = layerPoint.subtract(originPoint);
        return transformation.transform(afterSubtract, scaleRef.current);
      };
    } else if (isGeo) {
      return null;
    } else {
      return layer => {
        return L.point(layer._latlng.lat, layer._latlng.lng);
      };
    }
  }

  function addScale() {
    const polyline = new L.Draw.Polyline(leaflet, {
      shapeOptions: {
        color: '#FF0000',
        lineWidth: 1,
        fillColor: '#FF0000',
        fillOpacity: 1,
        opacity: 1,
        fill: 'none',
        dashArray: [8, 8],
      },
    });
    polyline.enable();
    polyline.addVertex = overrideVortex;
    let element = document.createElement('div');
    leaflet.on('draw:created', e => {
      const mark = e.layer;
      const latLngs = mark.getLatLngs();
      mark.addTo(leaflet);
      ReactDOM.render(
        <Scale
          dist={leaflet.distance(latLngs[0], latLngs[1])}
          setFloorPlan={setFloorPlan}
          intl={intl}
          mark={mark}
          floorPlan={floorPlan}
          url={`/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}`}
          afterUpdate={afterUpdateScale}
        />,
        element
      );
      mark.bindPopup(element);
      mark.on('popupclose', () => {
        mark.remove();
      });
      mark.openPopup();
    });
  }

  function createMarkers(mark, leafMap, values) {
    const layers = [];
    leafMap.on('zoomend', () => {
      const currentZoom = leafMap.getZoom();
      const anchorIcon = icon({
        iconUrl: toAbsoluteUrl('/media/svg/senseworks/geo-anchor-icon.svg'),
        iconSize: [currentZoom * 10, currentZoom * 10],
      });
      mark.setIcon(anchorIcon);
    });
    const element = document.createElement('div');
    ReactDOM.render(
      <GeoAnchor
        intl={intl}
        hasPermission={hasPermission}
        onClose={() => {
          mark.closePopup();
        }}
        onDelete={async () => {
          const activeLayers = [];
          leafMap.eachLayer(lay => {
            if (lay instanceof Marker && !lay.anchor) activeLayers.push(lay);
          });
          if (mark.databaseId)
            await axios.delete(
              `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors/${mark.databaseId}`
            );
          mark.remove();
          if (activeLayers.length === 3) {
            setBoxes({
              1: { top: 0, left: 0, id: -1 },
            });
          } else if (activeLayers.length === 2) {
            setBoxes({
              1: { top: 0, left: 0, id: -1 },
              2: { top: 45, left: 0, id: -2 },
            });
          } else {
            setBoxes({
              1: { top: 0, left: 0, id: -1 },
              2: { top: 45, left: 0, id: -2 },
              3: { top: 90, left: 0, id: -3 },
            });
          }
          setPendencies(state => ({
            ...state,
            geoAnchors: true,
          }));
        }}
        onSubmit={async (values, { setStatus, setSubmitting }) => {
          mark.closePopup();
          const layers = [];
          leafMap.eachLayer(lay => {
            if (lay instanceof Marker && lay.databaseId) layers.push(lay);
          });
          if (!mark.databaseId) {
            const formValues = {
              lat: parseFloat(values.lat.toFixed(6)),
              long: parseFloat(values.long.toFixed(6)),
              mapCoordinates: mark._latlng,
              leafletCoordinatesInMeters: null,
            };
            const transform = transformLatLng(true);
            if (transform) {
              const point = transform(mark);
              formValues.leafletCoordinatesInMeters = {
                x: point.x,
                y: point.y,
              };
            }
            const { data } = await axios.post(
              `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors`,
              formValues
            );
            mark.databaseId = data.id;
            mark.geoAnchor = {
              ...formValues,
              id: data.id,
            };
            if (layers.length === 2) {
              setPendencies(state => ({
                ...state,
                geoAnchors: false,
              }));
              if (!pendencies.initiator && !pendencies.bridge) {
                setOpen(state => ({
                  ...state,
                  alerts: false,
                }));
              }
            } else {
              setPendencies(state => ({
                ...state,
                geoAnchors: true,
              }));
            }
          } else {
            const formValues = {
              lat: parseFloat(values.lat.toFixed(6)),
              long: parseFloat(values.long.toFixed(6)),
              mapCoordinates: mark._latlng,
              leafletCoordinatesInMeters: null,
            };
            const transform = transformLatLng(true);
            if (transform) {
              const point = transform(mark);
              formValues.leafletCoordinatesInMeters = {
                x: point.x,
                y: point.y,
              };
            }
            await axios.put(
              `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors/${mark.databaseId}`,
              formValues
            );
          }
        }}
        values={{
          lat: values?.coordinates[0] ?? '',
          long: values?.coordinates[1] ?? '',
        }}
      />,
      element
    );
    const popup = L.responsivePopup().setContent(element);
    mark.bindPopup(popup, { minWidth: 220 });
    mark.on('click', () => {
      mark.openPopup();
    });

    leafMap.eachLayer(lay => {
      if (lay instanceof Marker && !lay.anchor) layers.push(lay);
    });
    if (layers.length === 3) setBoxes({});
    else if (layers.length === 2) setBoxes({ 1: { top: 0, left: 0, id: -1 } });
    else
      setBoxes({
        1: { top: 0, left: 0, id: -1 },
        2: { top: 45, left: 0, id: -2 },
      });
  }

  async function createAnchorMarkers(mark, leafMap) {
    leafMap.on('zoomend', () => {
      const currentZoom = leafMap.getZoom();
      const anchorIcon = icon({
        iconUrl: toAbsoluteUrl('/media/svg/senseworks/anchor-icon.svg'),
        iconSize: [currentZoom * 10, currentZoom * 10],
      });
      mark.setIcon(anchorIcon);
    });

    const el = document.createElement('div');
    const popup = L.responsivePopup().setContent(el);
    mark.bindPopup(popup, { minWidth: 220 });

    mark.on('click', async e => {
      e.target.closePopup();
      if (mark._popup) {
        didClickAnchorMarker(mark, leafMap);
      }
    });

    setSelectedThing(null);
  }

  function didClickAnchorMarker(mark, leafMap) {
    const element = document.createElement('div');
    const transform = transformLatLng();
    const layers = [];
    leafMap.eachLayer(lay => {
      if (lay.anchor) layers.push(null);
    });
    if (mark.anchor?.model?.startsWith('swxa')) {
      ReactDOM.render(
        <Anchor
          setPendencies={setPendencies}
          getMeters={transform}
          isUniqueAnchor={layers.length === 1}
          networks={networks}
          hasPermission={hasPermission}
          floorPlanId={floorPlanId}
          intl={intl}
          mark={mark}
          onRemove={removeAnchor}
          callback={isOrigin => {
            mark.closePopup();
            if (isOrigin) {
              mark.anchor.leafletCoordinatesInMeters = {
                x: 0,
                y: 0,
              };
              setOriginMarker(mark.anchor);
              updateLocations(mark);
              updateGeoAnchorsLocation(mark);
              updateZones(mark);
            }
          }}
        />,
        element
      );
    } else if (mark.anchor?.model?.startsWith('swxb')) {
      ReactDOM.render(
        <Bridge
          setPendencies={setPendencies}
          hasPermission={hasPermission}
          floorPlanId={floorPlanId}
          intl={intl}
          mark={mark}
          onRemove={removeAnchor}
          callback={() => {
            mark.closePopup();
          }}
        />,
        element
      );
    }
    mark._popup.setContent(element);
    mark.openPopup();
    async function removeAnchor(isOrigin) {
      await axios.put(`/things/${mark.anchor.thingName}`, {
        lat: null,
        lng: null,
        floorPlanId: null,
      });
      mark.remove();

      const newAvailable = availableThings.concat(mark.anchor);
      setAvailableThings(val => val.concat(mark.anchor));
      const newFloorPlans = floorPlanThings.filter(
        x => x.id !== mark.anchor.id
      );
      setFloorPlanThings(val => val.filter(x => x.id !== mark.anchor.id));
      if (filteredThings.length) {
        setFiltered(newAvailable, newFloorPlans, inputController.current);
      }

      if (isOrigin) {
        await Promise.all(
          zonesRef.current.map(zone => {
            return axios.put(
              `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/zones/${zone.id}`,
              {
                ...zone,
                geoJsonFromOrigin: null,
              }
            );
          })
        );
      }
    }
  }

  function updateLocations(mark) {
    const scale = scaleRef.current;
    const latLng = mark.getLatLng();
    const originPoint = L.point(latLng.lat, latLng.lng);
    leaflet.eachLayer(async lay => {
      if (
        lay instanceof Marker &&
        lay.anchor &&
        lay.anchor.id !== mark.anchor.id
      ) {
        const anchor = lay.anchor;
        const layLatLng = lay.getLatLng();
        const point = L.point(layLatLng.lat, layLatLng.lng);
        const dist = transformation.transform(
          point.subtract(originPoint),
          scale
        );
        lay.anchor.leafletCoordinatesInMeters = {
          x: dist.x,
          y: dist.y,
        };
        updateLocation(anchor, lay, dist);
      }
    });
  }

  function updateGeoAnchorsLocation(mark) {
    const scale = scaleRef.current;
    const latLng = mark.getLatLng();
    const originPoint = L.point(latLng.lat, latLng.lng);
    leaflet.eachLayer(async lay => {
      if (lay instanceof Marker && lay.databaseId) {
        const geoAnchor = lay.geoAnchor;
        if (!geoAnchor.lat && !geoAnchor.long) {
          const coordinates = JSON.parse(geoAnchor.coordinates).coordinates;
          geoAnchor.lat = coordinates[0];
          geoAnchor.long = coordinates[1];
        }
        const layLatLng = lay.getLatLng();
        geoAnchor.mapCoordinates = lay._latlng;
        updateGeoAnchorLocation(geoAnchor, layLatLng, scale, originPoint);
      }
    });
  }

  function updateZones(mark) {
    const latLng = mark.getLatLng();
    const originPoint = L.point(latLng.lat, latLng.lng);
    zonesRef.current.forEach(zone => {
      let distance = [];
      const geometry = zone.geoJson.geometry;
      if (geometry.type === 'Point') {
        const point = L.point(geometry.coordinates[1], geometry.coordinates[0]);
        const dist = transformation.transform(
          point.subtract(originPoint),
          scaleRef.current
        );
        zone.geoJsonFromOrigin = JSON.parse(JSON.stringify(zone.geoJson));
        zone.geoJsonFromOrigin.properties = {
          radius: zone.geoJson.properties.radius * scaleRef.current,
        };
        zone.geoJsonFromOrigin.geometry.coordinates[0] = dist.x;
        zone.geoJsonFromOrigin.geometry.coordinates[1] = dist.y;
      } else {
        head(geometry.coordinates).forEach(x => {
          const point = L.point(x[1], x[0]);
          distance.push(
            transformation.transform(
              point.subtract(originPoint),
              scaleRef.current
            )
          );
        });
        zone.geoJsonFromOrigin = JSON.parse(JSON.stringify(zone.geoJson));
        zone.geoJsonFromOrigin.geometry.coordinates[0] = distance.map(
          ({ x, y }) => [x, y]
        );
      }
      axios.put(
        `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/zones/${zone.id}`,
        zone
      );
    });
  }

  function getDistFromOrigin(zone) {
    let mark;
    leaflet.eachLayer(lay => {
      if (lay.anchor) {
        const anchor = lay.anchor;
        if (
          anchor.leafletCoordinatesInMeters?.x === 0 &&
          anchor.leafletCoordinatesInMeters?.y === 0
        ) {
          mark = lay;
        }
      }
    });
    if (!mark) return null;
    const latLng = mark.getLatLng();
    const originPoint = L.point(latLng.lat, latLng.lng);
    const geometry = zone.geoJson.geometry;
    if (geometry.type === 'Point') {
      const point = L.point(geometry.coordinates[1], geometry.coordinates[0]);
      const dist = transformation.transform(
        point.subtract(originPoint),
        scaleRef.current
      );
      zone.geoJsonFromOrigin = JSON.parse(JSON.stringify(zone.geoJson));
      zone.geoJsonFromOrigin.properties = {
        radius: zone.geoJson.properties.radius * scaleRef.current,
      };
      zone.geoJsonFromOrigin.geometry.coordinates[0] = dist.x;
      zone.geoJsonFromOrigin.geometry.coordinates[1] = dist.y;
    } else {
      let distance = [];
      head(geometry.coordinates).forEach(x => {
        const point = L.point(x[1], x[0]);
        distance.push(
          transformation.transform(
            point.subtract(originPoint),
            scaleRef.current
          )
        );
      });
      zone.geoJsonFromOrigin = JSON.parse(JSON.stringify(zone.geoJson));
      zone.geoJsonFromOrigin.geometry.coordinates[0] = distance.map(
        ({ x, y }) => [x, y]
      );
    }
    return zone.geoJsonFromOrigin;
  }

  async function updateLocation(anchor, lay, dist) {
    const { data } = await axios.get(`/thing-shadows/${anchor.thingName}`);
    const { lat, lng } = lay.getLatLng();
    axios.put(`/things/${anchor.thingName}`, {
      lat: lat,
      lng: lng,
      floorPlanId,
      leafletCoordinatesInMeters: {
        x: dist.x,
        y: dist.y,
      },
    });
    if (data.uwb?.state) {
      const shadow = {
        ...data.uwb.state.desired,
        classic: data.classic?.state?.desired,
        connectivity: data.connectivity?.state?.desired ?? {},
      };
      delete shadow.label;
      axios.put(`/thing-shadows/${anchor.thingName}`, {
        oldValues: {
          ...shadow,
          positioning: {},
        },
        newValues: {
          ...shadow,
          positioning: {
            x: +dist.x.toFixed(3),
            y: +dist.y.toFixed(3),
            z: get(shadow, 'positioning.z', 0),
          },
          networkId: networks[0].networkId,
        },
      });
    }
  }

  function updateGeoAnchorLocation(anchor, layLatLng, scale, originPoint) {
    const point = L.point(layLatLng.lat, layLatLng.lng);
    const dist = transformation.transform(point.subtract(originPoint), scale);
    anchor.leafletCoordinatesInMeters = {
      x: dist.x,
      y: dist.y,
    };
    axios.put(
      `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/geo-anchors/${anchor.id}`,
      anchor
    );
  }

  function validateImageData() {
    const img = new Image();
    img.onload = function() {
      setImage({ width: this.width, height: this.height, img });
    };
    img.src = url;
  }

  const formik = useFormik({
    initialValues: floorPlan,
    enableReinitialize: true,
    validationSchema: getSchema(intl),
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setSubmitting(true);
      try {
        const hasUpdatedFile = values.floorPlanURI !== floorPlan.floorPlanURI;
        values.processingGroupId = values.processingGroupId || null;
        if (hasUpdatedFile) values.croppedFloorPlanURI = '';
        if (values.processingGroupId) {
          setPendencies(state => ({
            ...state,
            processingGroup: false,
          }));
        } else {
          setPendencies(state => ({
            ...state,
            processingGroup: true,
          }));
        }
        await axios.put(
          `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}`,
          values
        );
        if (hasUpdatedFile)
          history.push(
            `/geo-planner/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}/crop`
          );
        else
          setOpen({ edit: false, zones: false, anchors: false, alerts: false });
        setPaths([
          { title: intl.formatMessage({ id: 'FLOORPLANS' }) },
          {
            title: intl.formatMessage({ id: 'AREAS.TITLE' }),
            pathname: '/geo-planner/areas',
          },
          {
            title: floorPlan.areaName,
            pathname: `/geo-planner/areas/${areaId}`,
          },
          {
            title: floorPlan.facilityName,
            pathname: `/geo-planner/areas/${areaId}/facilities/${facilityId}`,
          },
          {
            title: values.name,
            pathName: `/geo-planner/areas/${areaId}/facilities/${facilityId}/floorPlans/${floorPlanId}`,
          },
        ]);
      } finally {
        setSubmitting(false);
      }
    },
  });

  function toBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  async function fileUpload(file) {
    const config = {
      headers: {
        'content-type': 'application/pdf',
      },
    };
    const base64 = await toBase64(file);
    const { data } = await axios.post(
      '/upload-pdfs',
      { file: base64, name: file.name },
      config
    );
    if (data) {
      formik.setFieldValue('floorPlanURI', data.path);
      formik.setFieldTouched('floorPlanURI');
    }
  }

  async function deleteFloorPlan() {
    try {
      setIsLoading(true);
      await axios.delete(
        `/areas/${areaId}/facilities/${facilityId}/floor-plans/${floorPlanId}`
      );
      history.push(`/geo-planner/areas/${areaId}/facilities/${facilityId}`);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409)
        setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  }

  function setZones(zones) {
    zonesRef.current = zones;
  }

  function closeAlerts() {
    setOpen(state => ({
      ...state,
      alerts: false,
    }));
  }

  function didClickInstalledThing(anchor) {
    return () => {
      const currentZoom = leaflet.getZoom();
      const defaultAnchorIcon = icon({
        iconUrl: toAbsoluteUrl('/media/svg/senseworks/anchor-icon.svg'),
        iconSize: [currentZoom * 10, currentZoom * 10],
      });
      const installedAnchorIcon = icon({
        iconUrl: toAbsoluteUrl(
          '/media/svg/senseworks/anchor-installed-icon.svg'
        ),
        iconSize: [currentZoom * 10, currentZoom * 10],
      });
      leaflet.eachLayer(async mark => {
        if (mark.anchor?.id === anchor.id) {
          mark.setIcon(installedAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(defaultAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(installedAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(defaultAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(installedAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(defaultAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(installedAnchorIcon);
          await new Promise(resolve => setTimeout(resolve, 100));
          mark.setIcon(defaultAnchorIcon);
        }
      });
    };
  }

  function filterThings(val) {
    if (!val) {
      setFilteredThings([]);
      return;
    }
    setFiltered(availableThings, floorPlanThings, val);
  }

  function setFiltered(availableThingsParam, installedThings, val) {
    installedThings.forEach(val => (val.installed = true));
    availableThingsParam.forEach(val => (val.installed = false));
    const things = uniqBy([...availableThingsParam, ...installedThings], 'id');
    setFilteredThings(
      things.filter(x => x.attributes.serialNumber.includes(val))
    );
  }

  return (
    <MotionContainer
      id="floor-plan-container"
      className="card flex-row-fluid h-100"
    >
      <CardHeader
        title={intl.formatMessage(
          { id: 'NAME_VALUE' },
          { name: get(formik, 'values.name', ' ') }
        )}
        subtitle={intl.formatMessage({
          id: 'AREAS.FACILITIES.FLOORPLANS.NEW.SUBTITLE',
        })}
      />

      <div
        className="d-flex flex-sm-column flex-md-row flex-lg-row h-100 card-body"
        style={{ marginTop: -1 }}
        ref={el => el && el.style.setProperty('padding', '0', 'important')}
      >
        <Loading isLoading={isLoading} />
        <div
          className={`${
            open.anchors
              ? 'col-md-2 col-lg-2 px-0 d-flex col-12 animate-enter'
              : 'd-none'
          } flex-column`}
          style={{ border: 'solid 1px #AAAAAA' }}
        >
          <div
            className="card-header-bg p-4 d-flex justify-content-center align-items-center"
            style={{ borderBottom: 'solid 1px #AAAAAA' }}
          >
            <span className="svg svg-icon menu-icon mr-3">
              <SVG
                uniquifyIDs
                src={toAbsoluteUrl('/media/svg/senseworks/white-anchor.svg')}
              />
            </span>
            <h4 className="title font-size-h6 mb-0">
              {' '}
              {intl.formatMessage({ id: 'ANCHORS_AND_BRIDGES' })}{' '}
            </h4>
          </div>
          <div style={{ margin: '6px 8px 0 8px' }}>
            <SearchInput onChange={filterThings} set={inputController} />
          </div>
          <div className="d-flex flex-column floor-plan-aside-max-height">
            {!!filteredThings.length || !!inputController.current.length ? (
              <>
                {filteredThings.map(anc =>
                  anc.installed ? (
                    <InstalledThingCard
                      key={anc.id}
                      onClick={didClickInstalledThing(anc)}
                      thing={anc}
                    />
                  ) : (
                    <ThingCard
                      key={anc.id}
                      {...anc}
                      setDragging={setIsDragging}
                      setSelectedThing={setSelectedThing}
                    />
                  )
                )}
              </>
            ) : (
              <>
                {Object.keys(boxes).map(key => (
                  <GeoAnchorCard
                    key={key}
                    setDragging={setIsDragging}
                    {...boxes[key]}
                  />
                ))}
                {availableThings.map(anc => (
                  <ThingCard
                    key={anc.id}
                    {...anc}
                    setDragging={setIsDragging}
                    setSelectedThing={setSelectedThing}
                  />
                ))}
                {!!floorPlanThings.length && <Divider className="my-2" />}
                {floorPlanThings.map(anc => (
                  <InstalledThingCard
                    key={anc.id}
                    onClick={didClickInstalledThing(anc)}
                    thing={anc}
                  />
                ))}
              </>
            )}
          </div>
        </div>
        <AlertViewSideMenu
          isOpen={open.alerts}
          pendencies={pendencies}
          closeMenu={closeAlerts}
        />
        <div
          className={`col-12 ${
            open.anchors || open.alerts
              ? 'col-md-10 col-lg-10'
              : 'col-md-12 col-lg-12'
          } px-0 d-flex flex-column`}
        >
          <div
            className="col-12 py-2 d-flex space-between"
            style={{ flex: '0 1 auto' }}
          >
            <div className="d-flex">
              {(pendencies.bridge ||
                pendencies.initiator ||
                pendencies.geoAnchors ||
                pendencies.processingGroup) &&
                !open.alerts && (
                  <Button
                    classes="d-flex align-items-center justify-content-center mr-2 attention-bg"
                    logoSrc={'/media/svg/senseworks/alert-white.svg'}
                    type="button"
                    onClick={() =>
                      setOpen(val => ({
                        zones: false,
                        edit: false,
                        anchors: false,
                        alerts: !val.alerts,
                      }))
                    }
                    title={intl.formatMessage({ id: 'ATTENTION' })}
                  />
                )}
              <Button
                classes="d-flex align-items-center justify-content-center"
                logoSrc={'/media/svg/senseworks/anchor.svg'}
                type="button"
                onClick={() =>
                  setOpen(val => ({
                    zones: false,
                    edit: false,
                    anchors: !val.anchors,
                    alerts: false,
                  }))
                }
                title={intl.formatMessage({ id: 'ANCHORS_AND_BRIDGES' })}
              />
            </div>
            <div className="d-flex">
              {hasPermission('zones*', 'R') && (
                <Button
                  classes="d-flex align-items-center justify-content-center ml-3"
                  logoSrc={'/media/svg/senseworks/zone.svg'}
                  type="button"
                  onClick={() =>
                    setOpen({
                      zones: true,
                      edit: false,
                      anchors: false,
                      alerts: false,
                    })
                  }
                  title={intl.formatMessage({ id: 'ZONES' })}
                />
              )}
              {hasPermission('floor-plans*', 'R') && (
                <Button
                  classes="d-flex align-items-center justify-content-center ml-3 mr-1"
                  logoSrc={'/media/svg/senseworks/floormap.svg'}
                  type="button"
                  onClick={() =>
                    setOpen({
                      zones: false,
                      edit: true,
                      anchors: false,
                      alerts: false,
                    })
                  }
                  title={intl.formatMessage({ id: 'EDIT' })}
                />
              )}
            </div>
          </div>
          <div className="w-100 flex-grow-1" ref={drop}>
            <div id="map" className="w-100 h-100" ref={mapRef}></div>
          </div>
        </div>
      </div>
      {hasPermission('floor-plans*', 'R') && (
        <EditFloorPlanSideMenu
          open={open.edit}
          onClose={() =>
            setOpen({
              edit: false,
              zones: false,
              anchors: false,
              alerts: false,
            })
          }
          floorPlan={floorPlan}
          formik={formik}
          onDrop={fileUpload}
          onDelete={deleteFloorPlan}
          hasPermission={hasPermission}
          addScale={addScale}
        />
      )}
      {image && hasPermission('zones*', 'R') ? (
        <EditZoneSideMenu
          isDragging={isDragging}
          getDistFromOrigin={getDistFromOrigin}
          storeZones={setZones}
          hasPermission={hasPermission}
          open={open.zones}
          mapRef={leaflet}
          onClose={() =>
            setOpen({
              edit: false,
              zones: false,
              anchors: false,
              alerts: false,
            })
          }
        />
      ) : null}
      <ErrorDialog
        onClose={() => setModalOpen(false)}
        open={isModalOpen}
        message="ERRORS.FLOORPLANS.CONFLICT.ZONE"
      />
    </MotionContainer>
  );
}

export default injectIntl(FloorPlansEdit);
